import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { billingApi } from "@/api/client";

// "awaiting_card": the Benchmark ended with no card on file; saving one converts it.
export type TrialStatus = "not_started" | "active" | "converted" | "declined" | "awaiting_card";

export interface TrialStatusResponse {
  trial_status: TrialStatus;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  trial_converted_at?: string | null;
  trial_declined_at?: string | null;
  days_remaining: number;
  grace_deadline_at: string | null;
  can_convert: boolean;
  can_decline: boolean;
  // Converting needs a card; without one the banner offers "Add a card".
  card_on_file: boolean;
  projected_savings_usd: string;
  projected_savings_pct: number;
  trial_request_count: number;
  successful_request_count: number;
  errored_request_count: number;
  blocked_request_count: number;
  shadowed_request_count: number;
  coverage_pct: number;
  // Of the successful requests, how many fired a REAL shadow call (vs a
  // deterministic estimate). Reflects the per-tenant trial_shadow_sample_rate.
  real_shadow_request_count: number;
  real_shadow_coverage_pct: number;
  cap_reached: boolean;
  cap_reached_at: string | null;
  shadow_cost_accrued_usd: string;
  shadow_cost_cap_usd: string;
}

const TRIAL_STATUS_KEY = ["billing-trial-status"];

export function useTrialStatus(tenantId: string) {
  return useQuery({
    queryKey: [...TRIAL_STATUS_KEY, tenantId],
    queryFn: () =>
      billingApi.get("trial/status").json<TrialStatusResponse>(),
    enabled: !!tenantId,
    staleTime: 10_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

export function useConvertTrial(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      billingApi.post("trial/convert").json<TrialStatusResponse>(),
    onSuccess: (data) => {
      qc.setQueryData([...TRIAL_STATUS_KEY, tenantId], data);
      qc.invalidateQueries({ queryKey: ["billing-v2-current-period"] });
      qc.invalidateQueries({ queryKey: ["billing-v2-periods"] });
      // Conversion resets the dashboard: KPIs and charts now count only
      // post-conversion activity (the server windows them from
      // trial_converted_at). Refetch so the user sees the clean slate
      // immediately. The request log and exports keep the full history and
      // are intentionally left alone.
      qc.invalidateQueries({ queryKey: ["usage-summary"] });
      qc.invalidateQueries({ queryKey: ["usage-savings"] });
      qc.invalidateQueries({ queryKey: ["savings-breakdown"] });
      qc.invalidateQueries({ queryKey: ["routing-metrics"] });
    },
  });
}

export function useDeclineTrial(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      billingApi.post("trial/decline").json<TrialStatusResponse>(),
    onSuccess: (data) => {
      qc.setQueryData([...TRIAL_STATUS_KEY, tenantId], data);
      qc.invalidateQueries({ queryKey: ["billing-v2-current-period"] });
    },
    onError: () => {
      // 409 from grace-window expiry mid-click — snap UI to current truth.
      qc.invalidateQueries({ queryKey: TRIAL_STATUS_KEY });
    },
  });
}
